/**
 * Компонент списка событий - Enterprise Design v2.0
 * QRIUM Recvisit
 */
import { useState, useEffect, useCallback } from 'react'
import { EventsRepo } from '../offline/eventsRepo'
import { EmployeesRepo } from '../offline/employeesRepo'
import type { EventRecord as Event } from '../offline/db'

interface EventWithEmployee extends Event {
  employee_name?: string
}

export function EventList() {
  const [events, setEvents] = useState<EventWithEmployee[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    date: '',
    employeeId: '',
    eventType: '',
  })

  const loadEvents = useCallback(async () => {
    setIsLoading(true)
    try {
      const allEvents = await EventsRepo.getAll({ limit: 1000 })

      const eventsWithNames: EventWithEmployee[] = await Promise.all(
        allEvents.map(async (event) => {
          const employeeData = await EmployeesRepo.getFullData(event.employee_id)
          return {
            ...event,
            employee_name: employeeData?.full_name || `ID: ${event.employee_id.substring(0, 8)}`,
          }
        })
      )

      let filteredEvents = eventsWithNames

      if (filters.date) {
        const filterDate = new Date(filters.date)
        filteredEvents = filteredEvents.filter((event) => {
          const eventDate = new Date(event.happened_at)
          return (
            eventDate.getDate() === filterDate.getDate() &&
            eventDate.getMonth() === filterDate.getMonth() &&
            eventDate.getFullYear() === filterDate.getFullYear()
          )
        })
      }

      if (filters.employeeId) {
        filteredEvents = filteredEvents.filter((event) =>
          event.employee_id.toLowerCase().includes(filters.employeeId.toLowerCase()) ||
          event.employee_name?.toLowerCase().includes(filters.employeeId.toLowerCase())
        )
      }

      if (filters.eventType) {
        filteredEvents = filteredEvents.filter((event) => event.event_type === filters.eventType)
      }

      filteredEvents.sort((a, b) => {
        return new Date(b.happened_at).getTime() - new Date(a.happened_at).getTime()
      })

      setEvents(filteredEvents)
    } catch (error) {
      console.error('Failed to load events:', error)
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    })
  }

  const hasActiveFilters = filters.date || filters.employeeId || filters.eventType
  const syncedCount = events.filter((e) => e.synced).length
  const pendingCount = events.filter((e) => !e.synced && !e.sync_failed).length
  const errorCount = events.filter((e) => e.sync_failed).length

  return (
    <div className="space-y-section">
      {/* Stats Grid - 4 cards in 2x2 layout */}
      <div className="stats-grid-4">
        <div className="stat-card">
          <div className="stat-card-value text-white">{events.length}</div>
          <div className="stat-card-label">Всего</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value text-[#22c55e]">{syncedCount}</div>
          <div className="stat-card-label">Синхронизировано</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value text-[#f59e0b]">{pendingCount}</div>
          <div className="stat-card-label">Ожидает</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value text-[#ef4444]">{errorCount}</div>
          <div className="stat-card-label">Ошибки</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button onClick={loadEvents} className="btn btn-md btn-secondary flex-1">
          <span>🔄</span>
          <span>Обновить</span>
        </button>
        <button 
          onClick={() => setShowFilters(!showFilters)} 
          className={`btn btn-md flex-1 ${showFilters || hasActiveFilters ? 'btn-primary' : 'btn-secondary'}`}
        >
          <span>🔍</span>
          <span>Фильтры</span>
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-white ml-1" />
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="focus-block animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <span className="section-title">Фильтры</span>
            {hasActiveFilters && (
              <button 
                onClick={() => setFilters({ date: '', employeeId: '', eventType: '' })}
                className="text-[#3b82f6] text-[14px] font-medium"
              >
                Сбросить
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-[13px] text-[#a0a0a0] mb-2">Дата</label>
              <input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                className="input"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#a0a0a0] mb-2">Сотрудник</label>
              <input
                type="text"
                placeholder="Поиск по ФИО или ID..."
                value={filters.employeeId}
                onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}
                className="input"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#a0a0a0] mb-2">Тип события</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, eventType: '' })}
                  className={`btn btn-md flex-1 ${!filters.eventType ? 'btn-primary' : 'btn-secondary'}`}
                >
                  Все
                </button>
                <button
                  onClick={() => setFilters({ ...filters, eventType: 'check_in' })}
                  className={`btn btn-md flex-1 ${filters.eventType === 'check_in' ? 'btn-success' : 'btn-secondary'}`}
                >
                  📥 Приход
                </button>
                <button
                  onClick={() => setFilters({ ...filters, eventType: 'check_out' })}
                  className={`btn btn-md flex-1 ${filters.eventType === 'check_out' ? 'btn-danger' : 'btn-secondary'}`}
                >
                  📤 Уход
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loading-spinner mb-4" />
          <p className="text-[#a0a0a0] text-[14px]">Загрузка событий...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p className="empty-state-title">Нет событий</p>
          <p className="empty-state-description">
            {hasActiveFilters ? 'Попробуйте изменить фильтры' : 'События появятся после сканирования QR-кодов'}
          </p>
        </div>
      ) : (
        <div>
          <div className="section-header">
            <span className="section-title">Последние события</span>
            <span className="text-[13px] text-[#6b6b6b]">{events.length} записей</span>
          </div>
          <div className="space-y-items">
            {events.slice(0, 50).map((event) => (
              <EventRow 
                key={event.id} 
                event={event} 
                formatTime={formatTime}
                formatDate={formatDate}
              />
            ))}
            {events.length > 50 && (
              <div className="text-center py-3 text-[#6b6b6b] text-[13px]">
                Показаны последние 50 из {events.length} событий
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function EventRow({ event, formatTime, formatDate }: { 
  event: EventWithEmployee
  formatTime: (date: string) => string
  formatDate: (date: string) => string
}) {
  const isCheckIn = event.event_type === 'check_in'
  
  return (
    <div className="event-item-spaced">
      {/* Icon */}
      <div className={`
        list-item-icon
        ${isCheckIn ? 'bg-[#22c55e]/15 text-[#22c55e]' : 'bg-[#ef4444]/15 text-[#ef4444]'}
      `}>
        {isCheckIn ? '📥' : '📤'}
      </div>
      
      {/* Content */}
      <div className="list-item-content">
        <p className="list-item-title truncate">
          {event.employee_name}
        </p>
        <p className="list-item-subtitle">
          {formatDate(event.happened_at)} • {formatTime(event.happened_at)}
          {event.source === 'manual' && <span className="ml-2 text-[#6b6b6b]">• Ручная</span>}
        </p>
      </div>
      
      {/* Status Badge */}
      <div className="flex-shrink-0">
        {event.synced ? (
          <span className="badge badge-success">✓</span>
        ) : event.sync_failed ? (
          <span className="badge badge-error">!</span>
        ) : (
          <span className="badge badge-warning">⏳</span>
        )}
      </div>
    </div>
  )
}
